import classNames from 'classnames';
import {
  DetailedHTMLProps, FC, InputHTMLAttributes, useEffect, useMemo, useState,
} from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { defaultTopLevelDomains, run } from 'mailcheck';
import type { FieldValidator } from 'final-form';
import getClassNames from 'src/common/utils/getClassNames';
import debounce from 'lodash.debounce';
import { useTranslation } from 'next-i18next';
import styles from './Input.module.scss';

defaultTopLevelDomains.push('co.za'); // extend existing TLDs

type FinalFormFieldProps = {
  validate?: FieldValidator<any>;
};

export type TextInputProps =
  Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'ref'> &
  {
    hasError?: boolean;
    /**
     * Purpose of parse function is to make sure that value should be removed from
     * values object if it's empty string
     * Reference: https://github.com/final-form/react-final-form/issues/130
     */
    parse?: (value: string) => string;
    allowAutoComplete?: boolean;
  } & FinalFormFieldProps;

const EmailInput: FC<TextInputProps> = ({
  name,
  hasError,
  parse = (value) => value,
  allowAutoComplete = true,
  ...props
}) => {
  const [t] = useTranslation('common');
  const { change } = useForm();
  const [suggestedEmail, setSuggestedEmail] = useState('');

  // NOTE: this doesn't really use the suggested warning functionality
  // in the react-final-form docs, nor does it mimic the old redux-form
  // API, but it seems sufficient for this use case. If we decide that
  // warning functionality is useful enough across the codebase,
  // we can re-implement it as a reusable hook or something to that effect.
  const warn = useMemo(
    () => (
      debounce(
        (email) => {
          run({
            email,
            suggested: ({ full }: { full: string }) => setSuggestedEmail(full),
            empty: () => setSuggestedEmail(''),
          });
        },
        1000, // ms
      )
    ),
    [],
  );
  useFormState({
    subscription: { values: true },
    onChange: ({ values }) => {
      if (props.disabled) return;

      if (values) {
        const { email } = values;
        warn(email);
      }
    },
  });
  useEffect(
    // This theoretically says "when warn changes or the component unmounts,
    // cancel the warning function". Need to test this.
    () => () => warn.cancel(),
    [warn],
  );

  return (
    <>
      <Field
        parse={parse}
        type="email"
        {...props}
        title={props.placeholder}
        component="input"
        autoComplete={allowAutoComplete ? 'on' : 'off'}
        name={name || ''}
        className={getClassNames(styles, 'input', classNames({ hasError }))}
      />
      {suggestedEmail && (
        <button
          className={styles.suggestion}
          onClick={() => {
            change(name || 'email', suggestedEmail);
            setSuggestedEmail('');
          }}
          type="button"
        >
          {t('email-input--email-suggestion', { suggestedEmail })}
        </button>
      )}
    </>
  );
};

export default EmailInput;
